import React, { useState, useEffect } from 'react';
import { paymentService } from '../../services/payments';
import Alert from '../../components/Common/Alert';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import BankDetailsModal from '../../components/BankDetailsModal';

const PaymentsManagement = () => {
  const [payments, setPayments] = useState([]);
  const [withdrawalRequests, setWithdrawalRequests] = useState([]);
  const [bankDetails, setBankDetails] = useState(null);
  const [stats, setStats] = useState({
    total_earnings: 0,
    available_balance: 0,
    pending_withdrawals: 0,
    total_withdrawals: 0,
    this_month_earnings: 0
  });
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [showBankModal, setShowBankModal] = useState(false);

  useEffect(() => {
    fetchPaymentsData();
  }, []);

  const fetchPaymentsData = async () => {
    try {
      setLoading(true);
      const [paymentsData, withdrawalsData, statsData, bankDetailsData] = await Promise.all([
        paymentService.getPaymentHistory(),
        paymentService.getWithdrawalRequests(),
        paymentService.getPaymentStats(),
        paymentService.getBankDetails().catch(() => null)
      ]);

      setPayments(Array.isArray(paymentsData) ? paymentsData : (paymentsData?.results || paymentsData?.payments || []));
      setWithdrawalRequests(Array.isArray(withdrawalsData) ? withdrawalsData : (withdrawalsData?.results || withdrawalsData?.withdrawals || []));
      setStats(statsData || {});

      if (bankDetailsData && bankDetailsData.ifsc_code) {
        setBankDetails({
          ...bankDetailsData,
          ifsc_code: bankDetailsData.ifsc_code.toUpperCase()
        });
      } else {
        setBankDetails(bankDetailsData);
      }
    } catch (error) {
      console.error('Error fetching payments data:', error);
      setAlert({
        type: 'danger',
        message: 'Failed to load payments data. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleWithdrawalRequest = async (e) => {
    e.preventDefault();
    const amount = parseFloat(withdrawalAmount);
    if (!amount || amount < 10000 || amount > stats.available_balance) {
      setAlert({
        type: 'danger',
        message: 'Please enter a valid amount (Min ₹10,000).'
      });
      return;
    }

    if (!bankDetails || !bankDetails.account_number) {
      setAlert({
        type: 'warning',
        message: 'Please add your bank details first.'
      });
      setShowBankModal(true);
      return;
    }

    try {
      setRequesting(true);
      await paymentService.requestWithdrawal({
        amount,
        bank_account_number: bankDetails.account_number,
        confirm_account_number: bankDetails.account_number,
        bank_ifsc_code: bankDetails.ifsc_code,
        confirm_ifsc_code: bankDetails.ifsc_code,
        bank_name: bankDetails.bank_name,
        account_holder_name: bankDetails.account_holder_name,
        mobile_number: bankDetails.mobile_number || ''
      });

      setAlert({
        type: 'success',
        message: 'Withdrawal request submitted successfully!'
      });
      setWithdrawalAmount('');
      fetchPaymentsData();
    } catch (error) {
      console.error('Error requesting withdrawal:', error);
      setAlert({
        type: 'danger',
        message: error.response?.data?.error || 'Failed to request withdrawal. Please try again.'
      });
    } finally {
      setRequesting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)', text: 'Completed' },
      pending: { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)', text: 'Pending' },
      approved: { color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)', text: 'Approved' },
      rejected: { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)', text: 'Rejected' },
      processing: { color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.1)', text: 'Processing' }
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider" style={{ backgroundColor: config.bg, color: config.color }}>
        {config.text}
      </span>
    );
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen p-4 sm:p-6" style={{ backgroundColor: '#0a0f1e' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-1">Finance Console</h2>
          <p className="text-[10px] sm:text-xs uppercase font-bold tracking-wider" style={{ color: '#64748b' }}>
            Manage earnings and withdrawals
          </p>
        </div>

        {alert && (
          <div className="mb-6">
            <Alert
              type={alert.type}
              message={alert.message}
              onClose={() => setAlert(null)}
            />
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total Earnings', value: stats.total_earnings, icon: 'bi-graph-up-arrow', color: '#3b82f6' },
            { label: 'Available Balance', value: stats.available_balance, icon: 'bi-wallet2', color: '#10b981' },
            { label: 'Pending Withdrawals', value: stats.pending_withdrawals, icon: 'bi-clock-history', color: '#f59e0b' },
            { label: 'This Month', value: stats.this_month_earnings, icon: 'bi-calendar-check', color: '#ef4444' }
          ].map((stat, index) => (
            <div key={index} className="rounded-2xl p-6 relative overflow-hidden" style={{ backgroundColor: '#161e35' }}>
              <div className="absolute top-0 right-0 p-4 opacity-10">
                <i className={`bi ${stat.icon}`} style={{ fontSize: '60px', color: stat.color }}></i>
              </div>
              <div className="relative z-10">
                <p className="text-xs uppercase font-bold tracking-wider mb-2" style={{ color: '#64748b' }}>{stat.label}</p>
                <p className="text-3xl font-bold" style={{ color: stat.color }}>{formatCurrency(stat.value)}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Withdrawal Form */}
          <div className="lg:col-span-2 rounded-2xl p-6" style={{ backgroundColor: '#161e35' }}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <i className="bi bi-cash-stack text-xl" style={{ color: '#ef4444' }}></i>
                <h5 className="text-lg font-bold text-white">Request Withdrawal</h5>
              </div>
              {bankDetails && (
                <button
                  className="px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all"
                  style={{ backgroundColor: '#0f1420', color: '#94a3b8' }}
                  onClick={() => setShowBankModal(true)}
                >
                  <i className="bi bi-pencil mr-2"></i>
                  Edit Bank Details
                </button>
              )}
            </div>

            {bankDetails ? (
              <form onSubmit={handleWithdrawalRequest}>
                {/* Amount Input */}
                <div className="mb-6">
                  <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#64748b' }}>
                    Withdrawal Amount
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg font-bold" style={{ color: '#ef4444' }}>₹</span>
                    <input
                      type="number"
                      className="w-full pl-10 pr-4 py-3 rounded-lg text-lg font-bold outline-none transition-all"
                      style={{
                        backgroundColor: '#0f1420',
                        color: '#f8fafc',
                        border: 'none'
                      }}
                      placeholder="Enter amount (Min ₹10,000)"
                      value={withdrawalAmount}
                      onChange={(e) => setWithdrawalAmount(e.target.value)}
                      min="10000"
                      required
                    />
                  </div>
                  <p className="text-xs mt-2" style={{ color: '#64748b' }}>
                    Available balance: <span className="font-bold" style={{ color: '#10b981' }}>{formatCurrency(stats.available_balance)}</span>
                  </p>
                </div>

                {/* Bank Details Display */}
                <div className="mb-6 p-4 rounded-xl" style={{ backgroundColor: '#0f1420' }}>
                  <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: '#64748b' }}>Bank Account Details</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs mb-1" style={{ color: '#64748b' }}>Account Holder</p>
                      <p className="text-sm font-bold text-white">{bankDetails.account_holder_name}</p>
                    </div>
                    <div>
                      <p className="text-xs mb-1" style={{ color: '#64748b' }}>Bank Name</p>
                      <p className="text-sm font-bold text-white">{bankDetails.bank_name}</p>
                    </div>
                    <div>
                      <p className="text-xs mb-1" style={{ color: '#64748b' }}>Account Number</p>
                      <p className="text-sm font-bold font-mono text-white">****{bankDetails.account_number?.slice(-4)}</p>
                    </div>
                    <div>
                      <p className="text-xs mb-1" style={{ color: '#64748b' }}>IFSC Code</p>
                      <p className="text-sm font-bold font-mono text-white">{bankDetails.ifsc_code}</p>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="w-full py-3 rounded-lg text-sm font-bold uppercase tracking-wider transition-all"
                  style={{
                    backgroundColor: '#ef4444',
                    color: '#fff'
                  }}
                  disabled={requesting}
                  onMouseEnter={(e) => !requesting && (e.currentTarget.style.backgroundColor = '#dc2626')}
                  onMouseLeave={(e) => !requesting && (e.currentTarget.style.backgroundColor = '#ef4444')}
                >
                  {requesting ? (
                    <>
                      <span className="inline-block animate-spin mr-2">⏳</span>
                      Processing...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-send mr-2"></i>
                      Request Withdrawal
                    </>
                  )}
                </button>
              </form>
            ) : (
              <div className="text-center py-12">
                <i className="bi bi-bank text-6xl mb-4" style={{ color: '#64748b' }}></i>
                <p className="text-white font-bold mb-2">No Bank Details Found</p>
                <p className="text-sm mb-6" style={{ color: '#64748b' }}>Add your bank account to request withdrawals</p>
                <button
                  className="px-6 py-3 rounded-lg text-sm font-bold uppercase tracking-wider transition-all"
                  style={{
                    backgroundColor: '#ef4444',
                    color: '#fff'
                  }}
                  onClick={() => setShowBankModal(true)}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ef4444'}
                >
                  <i className="bi bi-plus-circle mr-2"></i>
                  Add Bank Details
                </button>
              </div>
            )}
          </div>

          {/* Withdrawal Requests */}
          <div className="rounded-2xl p-6" style={{ backgroundColor: '#161e35' }}>
            <div className="flex items-center gap-3 mb-6">
              <i className="bi bi-clock-history text-xl" style={{ color: '#f59e0b' }}></i>
              <h5 className="text-lg font-bold text-white">Recent Requests</h5>
            </div>

            <div className="space-y-3">
              {withdrawalRequests.length > 0 ? (
                withdrawalRequests.slice(0, 5).map((request) => (
                  <div key={request.id} className="p-4 rounded-lg" style={{ backgroundColor: '#0f1420' }}>
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-lg font-bold" style={{ color: '#ef4444' }}>{formatCurrency(request.amount)}</span>
                      {getStatusBadge(request.status)}
                    </div>
                    <p className="text-xs" style={{ color: '#64748b' }}>{formatDate(request.created_at)}</p>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <i className="bi bi-inbox text-4xl mb-2" style={{ color: '#64748b' }}></i>
                  <p className="text-sm" style={{ color: '#64748b' }}>No withdrawal requests yet</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Payment History */}
        <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: '#161e35' }}>
          <div className="p-6 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
            <div className="flex items-center gap-3">
              <i className="bi bi-receipt text-xl" style={{ color: '#3b82f6' }}></i>
              <h5 className="text-lg font-bold text-white">Payment History</h5>
            </div>
            <span className="px-3 py-1 rounded-lg text-xs font-bold" style={{ backgroundColor: '#0f1420', color: '#94a3b8' }}>
              {payments.length} Transactions
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider" style={{ color: '#64748b' }}>Date</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider" style={{ color: '#64748b' }}>Order ID</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider" style={{ color: '#64748b' }}>Amount</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider" style={{ color: '#64748b' }}>Status</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider" style={{ color: '#64748b' }}>Type</th>
                </tr>
              </thead>
              <tbody>
                {payments.length > 0 ? (
                  payments.map((payment) => (
                    <tr key={payment.id} style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                      <td className="px-6 py-4">
                        <div className="text-sm text-white">{formatDate(payment.created_at).split(',')[0]}</div>
                        <div className="text-xs" style={{ color: '#64748b' }}>{formatDate(payment.created_at).split(',')[1]}</div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-mono text-white">#{payment.order_id || payment.id}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-bold" style={{ color: '#10b981' }}>{formatCurrency(payment.amount)}</span>
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(payment.status)}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-white">{payment.payment_type || 'Order'}</span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <i className="bi bi-inbox text-4xl" style={{ color: '#64748b' }}></i>
                        <p className="text-sm" style={{ color: '#64748b' }}>No payment history found</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Bank Details Modal */}
      {showBankModal && (
        <BankDetailsModal
          show={showBankModal}
          onClose={() => setShowBankModal(false)}
          onSuccess={() => {
            setShowBankModal(false);
            fetchPaymentsData();
          }}
          existingDetails={bankDetails}
        />
      )}
    </div>
  );
};

export default PaymentsManagement;
